import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { EApiMiddlewareMethods } from "../../network/networkHandler.types";
import customNetworHandler from "../../network/customNetworHandler";
import { cWingsServiceBaseURL } from "../../config/Api.config";
import AppConstants from "constants/AppConstants";
import { saveORExportFile } from "utils/helpers.utils";

interface ExportInitiatedDialogState {
  exportInitiated: boolean;
  reportId: string;
  loading?: boolean;
  error?: string;
  errorCode?: string;
  excelLoading?: boolean;
}

const initialState: ExportInitiatedDialogState = {
  exportInitiated: false,
  reportId: "",
  loading: false,
  error: "",
  errorCode: "",
  excelLoading: false,
};

export const exportToExcel = createAsyncThunk(
  "exportInitiatedDialog/exportToExcel",
  async (
    { payload, url }: { payload: object; url: string },
    { rejectWithValue }
  ) => {
    try {
      const request = {
        url: url,
        method: EApiMiddlewareMethods.POST,
        data: payload,
        responseType: "blob",
        headers: { generateResponse: false },
      } as any;
      const response = await customNetworHandler(request, cWingsServiceBaseURL);
      // Handle the blob here, do not return it to Redux
      const { data, headers } = response;
      // saveORExportFile should return only serializable values
      const { exportInitiated, requestId } = saveORExportFile(
        data,
        headers,
        "Report"
      );
      return { exportInitiated, requestId }; // Only serializable values
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

const exportInitiatedDialogSlice = createSlice({
  name: "exportInitiatedDialog",
  initialState,
  reducers: {
    showExportInitiatedDialog(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.exportInitiated = true;
      state.reportId = action.payload || "";
    },
    resetExportInitiatedDialog(state) {
      state.exportInitiated = false;
      state.reportId = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(exportToExcel.pending, (state) => {
        state.excelLoading = true;
        state.error = "";
      })
      .addCase(exportToExcel.fulfilled, (state, action) => {
        const { exportInitiated, requestId } = action.payload || {};
        state.excelLoading = false;
        state.errorCode = "";
        state.error = "";
        state.exportInitiated = exportInitiated;
        if (requestId) {
          state.reportId = requestId;
        }
      })
      .addCase(exportToExcel.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.excelLoading = false;
        state.errorCode = errorPayload
          ? errorPayload.error
          : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload
          ? errorPayload.message
          : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      });
  },
});

export const {
  showExportInitiatedDialog,
  resetExportInitiatedDialog,
} = exportInitiatedDialogSlice.actions;

export default exportInitiatedDialogSlice.reducer;
